import React, { useRef, useState } from 'react';
import { api, openReceipt, RECEIPT_TYPES, uploadReceipt } from '../lib/api';
import { fmtDate, money, today } from '../lib/format';
import type { Cost, Payment, Task } from '../lib/types';
import { Drawer, Failed, Loading, Stat, TaskRow, type Ctx } from '../main';
import { useResource } from '../lib/hooks';

const METHOD: Record<string, string> = {
  BankTransfer: 'Bank transfer', Cash: 'Cash', CreditCard: 'Card', Cheque: 'Cheque',
};

type Tab = 'payments' | 'costs' | 'receipts';

export function Finance({ ctx, tasks }: { ctx: Ctx; tasks: Task[] }): React.JSX.Element {
  const summary = useResource(() => api.financeSummary(), []);
  const payments = useResource(() => api.payments(), []);
  const costs = useResource(() => api.costs(), []);
  const leases = useResource(() => api.leases(), []);
  const [tab, setTab] = useState<Tab>('payments');
  const [adding, setAdding] = useState<'payment' | 'cost' | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (summary.loading) return <Loading />;
  if (summary.error) return <Failed error={summary.error} onRetry={summary.reload} />;
  const s = summary.data!;
  const net = Number(s.collected) - Number(s.spent);
  const moneyTasks = tasks.filter((t) => t.type !== 'Action').sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const lateIn = tasks.filter((t) => t.type === 'Income' && t.dueDate < today()).reduce((a, b) => a + b.amount, 0);

  const ps = payments.data?.payments ?? [];
  const cs = costs.data?.costs ?? [];
  const tenantOf = (p: Payment): string =>
    leases.data?.leases.find((l) => l.id === p.lease_id || l.tenantId === p.tenant_id)?.tenantName ?? '—';
  const receipts = [
    ...ps.filter((p) => p.receipt_id).map((p) => ({ id: p.receipt_id!, kind: 'Income' as const, amount: p.amount,
      currency: p.currency, date: p.received_on, what: `${p.kind} · ${tenantOf(p)}`, property: p.property_id, task: p.task_id })),
    ...cs.filter((c) => c.receipt_id).map((c) => ({ id: c.receipt_id!, kind: 'Cost' as const, amount: c.amount,
      currency: c.currency, date: c.paid_on, what: c.description || c.category, property: c.property_id, task: c.task_id })),
  ].sort((a, b) => (a.date < b.date ? 1 : -1));

  const view = async (id: string): Promise<void> => {
    setError(null);
    try { await openReceipt(id); } catch (e) { setError((e as Error).message); }
  };
  const ReceiptLink = ({ id }: { id: string | null }): React.JSX.Element =>
    id ? <button className="btn sm" onClick={() => void view(id)}>View</button> : <span className="sub">—</span>;

  return (
    <>
      <div className="grid g4" style={{ marginBottom: 16 }}>
        <Stat label="Collected" value={money(s.collected)} tone="good" foot={`${ps.length} payments`} />
        <Stat label="Spent" value={money(s.spent)} foot={`${cs.length} costs`} />
        <Stat label="Net" value={money(net)} tone={net >= 0 ? 'good' : 'hot'} foot="Across the whole portfolio" />
        <Stat label="Committed, unpaid" value={money(s.committed_out)}
          foot={`and ${money(s.expected_in)} expected in${lateIn ? ` — ${money(lateIn)} of it late` : ''}`}
          tone={lateIn ? 'hot' : undefined} />
      </div>

      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}

      {moneyTasks.length > 0 && (
        <>
          <h2 className="sec">Money tasks waiting</h2>
          {moneyTasks.slice(0, 8).map((t) => <TaskRow key={t.id} task={t} ctx={ctx} />)}
          {moneyTasks.length > 8 && <div className="sub">and {moneyTasks.length - 8} more in Tasks → Money.</div>}
        </>
      )}

      <h2 className="sec">Ledger</h2>
      <div className="bar">
        {(['payments', 'costs', 'receipts'] as const).map((t) => (
          <button key={t} className={`btn sm ${tab === t ? 'pri' : ''}`} onClick={() => setTab(t)}>
            {{ payments: 'Payments', costs: 'Costs', receipts: `Receipts · ${receipts.length}` }[t]}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn sm w" onClick={() => setAdding('payment')}>Add payment</button>
          <button className="btn sm w" onClick={() => setAdding('cost')}>Add cost</button>
        </span>
      </div>

      <div className="card">
        {tab === 'payments' && (
          <table className="responsive">
            <thead><tr><th>Received</th><th>Amount</th><th>Tenant</th><th>Property</th><th>Kind</th><th>Method</th><th>Source</th><th>Receipt</th></tr></thead>
            <tbody>{ps.map((p) => (
              <tr key={p.id}>
                <td data-label="Received" className="mono">{fmtDate(p.received_on)}</td>
                <td data-label="Amount" className="mono" style={{ color: 'var(--teal)' }}><b>+{money(p.amount, p.currency)}</b></td>
                <td data-label="Tenant">{p.tenant_id || !p.note ? tenantOf(p) : <span className="sub">{p.note} (left)</span>}</td>
                <td data-label="Property">{ctx.propertyName(p.property_id)}</td>
                <td data-label="Kind"><span className="tag t-mute">{p.kind}</span></td>
                <td data-label="Method">{METHOD[p.method] ?? p.method}</td>
                <td data-label="Source" className="tid">{p.import_id ? 'imported' : p.task_id ? 'task' : 'manual'}</td>
                <td data-label="Receipt"><ReceiptLink id={p.receipt_id} /></td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {tab === 'costs' && (
          <table className="responsive">
            <thead><tr><th>Paid</th><th>Amount</th><th>Property</th><th>Category</th><th>Description</th><th>Source</th><th>Receipt</th></tr></thead>
            <tbody>{cs.map((c: Cost) => (
              <tr key={c.id}>
                <td data-label="Paid" className="mono">{fmtDate(c.paid_on)}</td>
                <td data-label="Amount" className="mono" style={{ color: 'var(--coral)' }}><b>−{money(c.amount, c.currency)}</b></td>
                <td data-label="Property">{ctx.propertyName(c.property_id)}</td>
                <td data-label="Category"><span className="tag t-mute">{c.category}</span></td>
                <td data-label="Description">{c.description || '—'}</td>
                <td data-label="Source" className="tid">{c.import_id ? 'imported' : c.task_id ? 'task' : 'manual'}</td>
                <td data-label="Receipt"><ReceiptLink id={c.receipt_id} /></td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {tab === 'receipts' && (
          <div className="bd"><div className="grid g4">
            {receipts.map((r) => (
              <button key={`${r.kind}-${r.id}`} className="rcpt" onClick={() => void view(r.id)}>
                <span className="paper">{Array.from({ length: 6 }, (_, i) => <i key={i} />)}</span>
                <span className="between">
                  <b className="mono" style={{ color: r.kind === 'Income' ? 'var(--teal)' : 'var(--coral)' }}>
                    {r.kind === 'Income' ? '+' : '−'}{money(r.amount, r.currency)}
                  </b>
                  <span className={`tag t-${r.kind}`}>{r.kind}</span>
                </span>
                <span className="sub">{r.what}</span>
                <span className="sub">{ctx.propertyName(r.property)} · {fmtDate(r.date)}</span>
                <span className="tid">{r.task ? 'From a task' : 'Entered by hand'}</span>
              </button>
            ))}
          </div></div>
        )}
        {(tab === 'payments' ? ps : tab === 'costs' ? cs : receipts).length === 0 && (
          <div className="empty"><b>Nothing recorded</b>Close a money task, or add an entry by hand.</div>
        )}
      </div>

      {adding && (
        <ManualEntry ctx={ctx} kind={adding} leases={leases.data?.leases ?? []} onClose={() => setAdding(null)}
          onSaved={async () => {
            setAdding(null);
            await Promise.all([summary.reload(), payments.reload(), costs.reload()]);
            ctx.toast(adding === 'payment' ? 'Payment added' : 'Cost added');
          }} />
      )}
    </>
  );
}

/**
 * The exceptions: a utility bill, a correction, money taken before the system
 * existed. A receipt is asked for but not required — the money gate applies to
 * tasks, and a manual entry is marked as one everywhere it appears.
 */
function ManualEntry({ ctx, kind, leases, onClose, onSaved }: {
  ctx: Ctx; kind: 'payment' | 'cost'; leases: Array<{ id: string; tenantId: string; propertyId: string; tenantName: string | null; roomNumber: string | null; status: string }>;
  onClose: () => void; onSaved: () => Promise<void>;
}): React.JSX.Element {
  const live = leases.filter((l) => l.status !== 'Ended');
  const [leaseId, setLeaseId] = useState(live[0]?.id ?? '');
  const [propertyId, setPropertyId] = useState(ctx.properties[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [method, setMethod] = useState('BankTransfer');
  const [paymentKind, setPaymentKind] = useState('Rent');
  const [category, setCategory] = useState('Variable');
  const [description, setDescription] = useState('');
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [receiptName, setReceiptName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const attach = async (f: File): Promise<void> => {
    setBusy(true); setError(null);
    try { setReceiptId(await uploadReceipt(f)); setReceiptName(f.name); }
    catch (e) { setError(`Could not upload: ${(e as Error).message}`); }
    finally { setBusy(false); }
  };

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      if (kind === 'payment') {
        const lease = live.find((l) => l.id === leaseId);
        if (!lease) throw new Error('Choose the tenancy this payment is for');
        await api.addPayment({
          propertyId: lease.propertyId, leaseId: lease.id, tenantId: lease.tenantId,
          amount: Number(amount), receivedOn: date, method, kind: paymentKind,
          ...(receiptId ? { receiptId } : {}),
        });
      } else {
        await api.addCost({
          propertyId, amount: Number(amount), paidOn: date, category, description,
          ...(receiptId ? { receiptId } : {}),
        });
      }
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title={kind === 'payment' ? 'Add a payment' : 'Add a cost'}
      subtitle="Manual entry — most money arrives by completing a task" onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!(Number(amount) > 0) || busy || (kind === 'payment' && !leaseId)} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      {kind === 'payment' ? (
        <>
          <div className="field">
            <label htmlFor="m-lease">Tenancy</label>
            <select id="m-lease" value={leaseId} onChange={(e) => setLeaseId(e.target.value)}>
              {live.length === 0 && <option value="">No current tenancies</option>}
              {live.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.tenantName ?? 'Tenant'} · {ctx.propertyName(l.propertyId)}{l.roomNumber ? ` · Room ${l.roomNumber}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="frow">
            <div className="field">
              <label htmlFor="m-kind">Kind</label>
              <select id="m-kind" value={paymentKind} onChange={(e) => setPaymentKind(e.target.value)}>
                <option>Rent</option><option>Deposit</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="m-method">Method</label>
              <select id="m-method" value={method} onChange={(e) => setMethod(e.target.value)}>
                {Object.entries(METHOD).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="field">
            <label htmlFor="m-prop">Property</label>
            <select id="m-prop" value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
              {ctx.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="m-cat">Category</label>
            <select id="m-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="Initial">Initial — setting the unit up</option>
              <option value="Fixed">Fixed — every month</option>
              <option value="Variable">Variable — as it comes</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="m-desc">Description</label>
            <input id="m-desc" value={description} placeholder="DEWA, October" onChange={(e) => setDescription(e.target.value)} />
          </div>
        </>
      )}
      <div className="frow">
        <div className="field">
          <label htmlFor="m-amt">Amount (AED)</label>
          <input id="m-amt" type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="m-date">{kind === 'payment' ? 'Received on' : 'Paid on'}</label>
          <input id="m-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label>Receipt</label>
        <input ref={file} type="file" accept={RECEIPT_TYPES} hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void attach(f); }} />
        <button className="btn full" disabled={busy} onClick={() => file.current?.click()}>
          {receiptId ? `Attached: ${receiptName} — replace` : 'Attach a receipt'}
        </button>
        <div className="hint">Strongly advised. Without one, nobody can answer for this number later.</div>
      </div>
    </Drawer>
  );
}
